import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomDataGrid from "../../../components/common/CustomDataGrid/CustomDataGrid";
import CustomDialog from "../../../components/common/CustomDialog";
import CustomSwitch from "../../../components/common/CustomSwitch";
import CustomTextInput from "../../../components/common/CustomTextInput";
import CustomSelect from "../../../components/common/CustomSelect"; // Ensure correct path
import CustomRadioButton from "../../../components/common/CustomRadioButton/CustomRadioButton";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import { X } from "lucide-react";

const VariableTab = () => {
  const [variables, setVariables] = useState([
    { id: 1, aliasName: "Lead ID", tableName: "Lead", columnName: "Lead Name", isActive: true },
    { id: 2, aliasName: "Subscription Type", tableName: "Subscription", columnName: "sub_type", isActive: true },
    { id: 3, aliasName: "Lead ID", tableName: "Lead", columnName: "Lead Name", isActive: false },
    { id: 4, aliasName: "Subscription Type", tableName: "Subscription", columnName: "sub_type", isActive: false },
  ]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, data: null });
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    aliasName: Yup.string()
      .required("Variable name is required")
      .min(3, "Variable name must be at least 3 characters"),
    tableName: Yup.string().required("Table name is required"),
    columnName: Yup.string().required("Column name is required"),
  });

  const initialValues = {
    aliasName: "",
    tableName: "Lead",
    columnName: "Lead Name",
    isActive: false,
  };

  const tables = [
    { label: "Lead Table", value: "Lead" },
    { label: "Company", value: "Company" },
    { label: "Advisor", value: "Advisor" },
    { label: "Subscription", value: "Subscription" },
  ];

  const columnsByTable = {
    Lead: [
      { label: "Lead Name", value: "Lead Name" },
      { label: "Priority", value: "Priority" },
      { label: "Lead Type", value: "Lead Type" },
    ],
    Company: [],
    Advisor: [],
    Subscription: [{ label: "sub_type", value: "sub_type" }],
  };

  const columns = [
    { field: "id", headerName: "#", width: "80px", renderCell: (row) => <span className="text-gray-600">{row.id}</span> },
    { field: "aliasName", headerName: "Alias Name", width: "200px", renderCell: (row) => <span className="text-gray-700">{row.aliasName}</span> },
    { field: "tableName", headerName: "Table Name", width: "150px", renderCell: (row) => <span className="text-gray-700">{row.tableName}</span> },
    { field: "columnName", headerName: "Column Name", width: "200px", renderCell: (row) => <span className="text-gray-700">{row.columnName}</span> },
    {
      field: "actions",
      headerName: "Action",
      width: "200px",
      renderCell: (row) => (
        <div className="flex items-center gap-2">
          <CustomSwitch
            value={row.isActive}
            name={`variable-status-${row.id}`}
            onChange={() => handleStatusChange(row.id)}
          />
          <button
            onClick={() => handleEdit(row)}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            title="Edit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
            title="Delete"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-red-600">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  const handleStatusChange = (id) => {
    const variable = variables.find((v) => v.id === id);
    setConfirmDialog({ open: true, type: "status", data: { id, newStatus: !variable.isActive } });
  };

  const handleEdit = (variable) => {
    setEditData(variable);
    setIsFormOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({ open: true, type: "delete", data: { id } });
  };

  const handleConfirmAction = async () => {
    setIsLoading(true);
    try {
      if (confirmDialog.type === "delete") {
        setVariables((prev) => prev.filter((v) => v.id !== confirmDialog.data.id));
      } else if (confirmDialog.type === "status") {
        const { id, newStatus } = confirmDialog.data;
        setVariables((prev) =>
          prev.map((v) => (v.id === id ? { ...v, isActive: newStatus } : v))
        );
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
      setConfirmDialog({ open: false, type: null, data: null });
    }
  };

  const handleSubmit = (values, { resetForm }) => {
    setIsLoading(true);
    setTimeout(() => {
      if (editData) {
        setVariables((prev) =>
          prev.map((variable) =>
            variable.id === editData.id ? { ...variable, ...values } : variable
          )
        );
      } else {
        setVariables((prev) => [
          ...prev,
          {
            id: prev.length + 1,
            ...values,
          },
        ]);
      }
      handleCloseForm();
      resetForm();
      setIsLoading(false);
    }, 1000); // Simulating API call
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditData(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <CustomButton
          onClick={() => {
            setEditData(null);
            setIsFormOpen(true);
          }}
          label="Add New Variable"
          variant="contained"
        />
      </div>

      <CustomDataGrid
        rows={variables}
        columns={columns}
        keyId="id"
        perPageSize={5}
        className="bg-white rounded-lg shadow"
      />

      <CustomDialog
        open={isFormOpen}
        onClose={handleCloseForm}
        title=""
        className="!p-0"
      >
        <div className="w-[700px]">
          <div className="flex justify-between items-center bg-[#0051AD] px-4 py-3 rounded-t-lg">
            <span className="text-sm font-medium text-white">
              {editData ? "Edit Variable" : "New Variable"}
            </span>
            <button
              type="button"
              onClick={handleCloseForm}
              className="text-white hover:text-gray-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <Formik
            initialValues={editData || initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, touched, setFieldValue, values }) => (
              <Form className="p-4">
                <div className="space-y-4">
                  <div className="mb-4">
                    <CustomTextInput
                      name="aliasName"
                      label="Enter Variable Name"
                      placeholder="Enter variable name"
                      value={values.aliasName}
                      onChange={(e) => setFieldValue("aliasName", e.target.value)}
                      error={errors.aliasName && touched.aliasName}
                      errorMessage={errors.aliasName}
                    />
                  </div>
                  <div className="mb-4 flex gap-4">
                    <CustomSelect
                      name="tableName"
                      label="Select Table"
                      value={values.tableName}
                      onChange={(e) => {
                        setFieldValue("tableName", e.target.value);
                        // Reset columnName when tableName changes
                        setFieldValue("columnName", columnsByTable[e.target.value]?.[0]?.value || "");
                      }}
                      menuList={tables}
                      error={errors.tableName && touched.tableName}
                      errorMessage={errors.tableName}
                      className="flex-1"
                    />
                    <CustomSelect
                      name="columnName"
                      label="Select Column"
                      value={values.columnName}
                      onChange={(e) => setFieldValue("columnName", e.target.value)}
                      menuList={columnsByTable[values.tableName] || []}
                      error={errors.columnName && touched.columnName}
                      errorMessage={errors.columnName}
                      className="flex-1"
                      disabled={!columnsByTable[values.tableName]?.length}
                    />
                  </div>
                  {editData && (
                    <div className="mb-4">
                      <label className="text-[#282828] font-medium mb-2 block">Status</label>
                      <CustomRadioButton
                        name="isActive"
                        value={values.isActive}
                        inRow={true}
                        radioList={[
                          { label: "Active", value: true },
                          { label: "Inactive", value: false },
                        ]}
                        onChange={(e) => setFieldValue("isActive", e.target.value === "true")}
                      />
                    </div>
                  )}
                  <div className="flex justify-end gap-3 mt-6">
                    <CustomButton
                      type="button"
                      onClick={handleCloseForm}
                      label="Cancel"
                      variant="outlined"
                    />
                    {!editData && (
                      <CustomButton
                        type="submit"
                        label={isLoading ? "Loading..." : "Save"}
                        variant="contained"
                        disabled={isLoading}
                      />
                    )}
                    {!editData && (
                      <CustomButton
                        type="submit"
                        onClick={() => setFieldValue("isActive", true)}
                        label={isLoading ? "Loading..." : "Publish"}
                        variant="contained"
                        disabled={isLoading}
                      />
                    )}
                    {editData && (
                      <CustomButton
                        type="submit"
                        label={isLoading ? "Loading..." : "Save"}
                        variant="contained"
                        disabled={isLoading}
                      />
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </CustomDialog>

      <CustomDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null, data: null })}
        title="Are You Sure?"
        titleClassName="text-center"
        className=""
      >
        <div className="w-[400px] text-center">
          <p className="text-gray-600 text-left border-b pb-4">
            {confirmDialog.type === "delete"
              ? "Are you sure you want to delete this variable?"
              : "Are you sure you want to change the status of this variable?"}
          </p>
          <div className="flex justify-between gap-3 mt-4">
            <button
              className="py-2 px-4 bg-gray-200 font-semibold text-base w-[170px] rounded-md"
              onClick={() => setConfirmDialog({ open: false, type: null, data: null })}
            >
              Cancel
            </button>
            <button
              className="py-2 px-4 bg-blue-500 w-[170px] font-semibold text-base text-white rounded-md disabled:opacity-70"
              onClick={handleConfirmAction}
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Confirm"}
            </button>
          </div>
        </div>
      </CustomDialog>
    </div>
  );
};

export default VariableTab;